import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Activity, CreditCard, DatabaseZap, LockKeyhole, RadioTower, Server } from 'lucide-react';

const layers = [
  {
    icon: Server,
    title: 'RAM-only VPN routing',
    copy: 'VPN traffic is routed through RAM-only servers. PhantomPath does not retain browsing activity, DNS queries, VPN traffic content or message content in its customer database.',
  },
  {
    icon: LockKeyhole,
    title: 'Stateless, time-limited access',
    copy: 'VPN passes do not require a traditional customer account, password or email address. Access codes are time-limited and exist for the duration of the purchased session.',
  },
  {
    icon: RadioTower,
    title: 'Zero-retention communications content',
    copy: 'Message payloads processed by PhantomPath-controlled systems are automatically deleted server-side within 30 seconds of delivery. Standard messaging uses TLS and voice transport uses SRTP.',
  },
  {
    icon: CreditCard,
    title: 'Payment data isolation',
    copy: 'Payment providers retain their own transaction records. PhantomPath receives only the limited order information needed for provisioning, refunds, fraud prevention, accounting, support and legal obligations. It is not linked to VPN traffic or communications content.',
  },
  {
    icon: Activity,
    title: 'Operational logs are not activity logs',
    copy: 'Limited payment, provisioning, error, security, system and administrative events may be logged. These operational records are rotated, size-capped and cleaned up, and do not contain browsing activity, traffic content or message content.',
  },
  {
    icon: DatabaseZap,
    title: 'Purpose-limited verification',
    copy: 'Supported USA numbers may require mobile-number verification for telecom compliance, fraud prevention and activation. The submitted number is used for that purpose and is not intentionally associated with VPN traffic, session keys or message content.',
  },
];

const questions = [
  ['What does “zero logs” mean at PhantomPath?', 'It means PhantomPath does not log or retain browsing activity, DNS queries, VPN traffic content or communications content. It does not mean that a functioning service can hold no operational record whatsoever. Limited payment, provisioning, error, security, system and administrative events may exist for defined operational or legal purposes.'],
  ['Does a RAM-only VPN server store browsing history?', 'PhantomPath routes VPN traffic through RAM-only servers and does not retain browsing activity or VPN traffic logs in its customer database. RAM-only infrastructure reduces persistent storage at the VPN-routing layer.'],
  ['Can I use the VPN without creating a PhantomPath account?', 'Yes. You do not need to create a PhantomPath account to use a VPN pass. A time-limited access code is generated for the purchased duration. Your chosen payment provider may process the information required to complete the transaction.'],
  ['Are payment records connected to VPN traffic?', 'No. Payment and limited order records remain operationally separate from VPN traffic, browsing activity, session content and communications. Payment providers retain their own transaction records under their policies and legal obligations.'],
  ['What happens to virtual-number message content?', 'Message payloads processed by PhantomPath-controlled systems are automatically deleted server-side within 30 seconds of delivery. No message history is retained in the PhantomPath customer database. Telecom providers may have their own legally required processing and retention practices.'],
  ['Why can operational logs exist on a zero-log service?', 'Operational logs record events needed to provision and protect the platform, such as payment confirmation, errors, security events and administrative actions. They are distinct from user-activity logs because they do not contain browsing activity, VPN traffic content or message content.'],
  ['Is USA number verification linked to my VPN use?', 'PhantomPath processes the submitted number for telecom verification and service-protection purposes and does not intentionally associate it with VPN traffic, browsing activity, session keys or message content. Relevant verification providers may process it under their own terms.'],
  ['Does PhantomPath claim complete anonymity?', 'No system should promise absolute anonymity without qualification. PhantomPath makes specific, testable claims about data minimisation, no mandatory traditional VPN account, RAM-only routing, activity-log exclusions, communications-content deletion and payment-data isolation.'],
];

const PrivacyArchitecture = () => {
  const canonical = 'https://phantompathvpn.com/learn/privacy-architecture';
  const description = 'Technical explanation of PhantomPath RAM-only VPN routing, stateless access, zero-retention messaging, operational logs and payment-data isolation.';
  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'TechArticle',
        headline: 'How PhantomPath Privacy Architecture Works',
        description,
        url: canonical,
        author: { '@type': 'Organization', name: 'PhantomPathVPN Ltd' },
        publisher: { '@type': 'Organization', name: 'PhantomPathVPN Ltd', url: 'https://phantompathvpn.com/' },
      },
      {
        '@type': 'FAQPage',
        mainEntity: questions.map(([question, answer]) => ({
          '@type': 'Question', name: question,
          acceptedAnswer: { '@type': 'Answer', text: answer },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://phantompathvpn.com/' },
          { '@type': 'ListItem', position: 2, name: 'Zero-Log Manifesto', item: 'https://phantompathvpn.com/zero-log-manifesto' },
          { '@type': 'ListItem', position: 3, name: 'Privacy Architecture', item: canonical },
        ],
      },
    ],
  };

  return (
    <main className="min-h-screen bg-[#050505] px-4 pb-20 pt-28 text-white">
      <Helmet>
        <title>RAM-Only VPN & Zero-Log Architecture | PhantomPathVPN</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="How PhantomPath Privacy Architecture Works" />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <div className="mx-auto max-w-5xl">
        <header className="border-b border-white/10 pb-10">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#00ffc8]">Technical privacy architecture</p>
          <h1 className="mt-4 max-w-4xl text-3xl font-extrabold leading-tight sm:text-5xl">How PhantomPath minimises data without hiding the operational reality</h1>
          <p className="mt-5 max-w-3xl text-base leading-7 text-gray-300 sm:text-lg">
            “Zero logs” should describe what is not recorded, not pretend that payments, provisioning and security happen without any operational events. This page separates VPN activity, communications content, transaction data and system operations clearly.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/zero-log-manifesto" className="rounded-xl bg-[#00ffc8] px-5 py-3 text-sm font-bold text-[#03110d]">Read the Zero-Log Manifesto</Link>
            <Link to="/privacy-policy" className="rounded-xl border border-white/15 bg-white/[0.035] px-5 py-3 text-sm font-bold text-white">Read the Privacy &amp; Technical Policy</Link>
          </div>
        </header>

        <section className="py-12" aria-labelledby="architecture-layers">
          <h2 id="architecture-layers" className="text-2xl font-bold">The six layers of data separation</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {layers.map(({ icon: Icon, title, copy }) => (
              <article key={title} className="rounded-2xl border border-white/10 bg-white/[0.035] p-6">
                <Icon className="h-6 w-6 text-[#00ffc8]" />
                <h2 className="mt-4 text-lg font-bold">{title}</h2>
                <p className="mt-3 text-sm leading-7 text-gray-300">{copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-4xl" aria-labelledby="technical-questions">
          <h2 id="technical-questions" className="text-2xl font-bold">Technical privacy questions, answered directly</h2>
          <div className="mt-5 divide-y divide-white/10 border-y border-white/10">
            {questions.map(([question, answer]) => (
              <article key={question} className="py-6">
                <h2 className="text-lg font-bold text-white">{question}</h2>
                <p className="mt-2 leading-7 text-gray-300">{answer}</p>
              </article>
            ))}
          </div>
        </section>

        <aside className="mx-auto mt-12 max-w-4xl rounded-2xl border border-[#00ffc8]/20 bg-[#00ffc8]/[0.035] p-6">
          <h2 className="font-bold text-[#00ffc8]">The precise privacy promise</h2>
          <p className="mt-3 text-sm leading-7 text-gray-300">No PhantomPath account required for a VPN pass. No browsing, DNS-query, VPN-traffic or communications-content logs. Payment providers process the information required for their transactions, while limited PhantomPath operational records remain separated, purpose-bound and subject to retention controls.</p>
        </aside>
      </div>
    </main>
  );
};

export default PrivacyArchitecture;
